import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, catchError } from 'rxjs/operators';
import { AddPropertyAvailabilityRequest, PropertyAvailability, PropertyPricingRule, UpdatePropertyAvailabilityRequest } from '../models/property.model';
import { environment } from '../../../../environments/environment';

// Helper interface to store pre-parsed rules so we don't parse JSON 700+ times
interface ParsedPricingRule extends PropertyPricingRule {
  parsedDaysOfWeek: number[] | null;
}

@Injectable({ providedIn: 'root' })
export class AvailabilityService {
  private http = inject(HttpClient);

  private readonly apiUrl = environment.apiUrl;

  // 1. Core State
  private readonly propertyId$ = new BehaviorSubject<string | null>(null);

  // 2. Unified Data Stream: Guarantees prices and availability stay perfectly in sync.
  // Using switchMap ensures that if the ID changes, the old API request is immediately cancelled.
  readonly data$: Observable<{ prices: Map<string, number>; avail: PropertyAvailability[] }> = this.propertyId$.pipe(
    switchMap(id => {
      // CLEAR CACHE: If ID is null, emit empty maps instantly to wipe the calendar UI
      if (!id) {
        return of({ prices: new Map<string, number>(), avail: [] });
      }

      // FETCH NEW DATA
      return combineLatest([
        this.fetchAvailability(id),
        this.fetchRules(id)
      ]).pipe(
        map(([avail, rules]) => ({
          prices: this.buildPriceMap(avail, rules),
          avail: avail
        })),
        catchError(err => {
          console.error('Failed to load availability data', err);
          return of({ prices: new Map<string, number>(), avail: [] });
        })
      );
    }),
    // Share replay ensures multiple subscribers (Card, Calendar) don't trigger multiple HTTP calls
    shareReplay(1) 
  );

  // 3. Public Selectors (What your components actually subscribe to)
  readonly priceMap$: Observable<Map<string, number>> = this.data$.pipe(map(d => d.prices));
  readonly avail$: Observable<PropertyAvailability[]> = this.data$.pipe(map(d => d.avail));

  // --------------------------------------------------------------
  // Public API Methods
  // --------------------------------------------------------------
  
  /** * Sets the current property ID. 
   * IMPORTANT: Call `setPropertyId(null)` right before passing the new ID to clear stale data!
   */
  setPropertyId(id: string | null): void {
    this.propertyId$.next(id);
  }

  createPropertyAvailability(availability: AddPropertyAvailabilityRequest) {
    return this.http.post(`${this.apiUrl}/property/availability`, availability);
  }

  deletePropertyAvailability(id: number) {
    return this.http.delete(`${this.apiUrl}/property/availability/${id}`);
  }

  getPropertyAvailability(id: string): Observable<PropertyAvailability[]> {
    return this.http.get<PropertyAvailability[]>(`${this.apiUrl}/property/availability/property/${id}`);
  }

  getPropertyAvailabilityById(id: number): Observable<PropertyAvailability> {
    return this.http.get<PropertyAvailability>(`${this.apiUrl}/property/availability/${id}`);
  }

  updatePropertyAvailability(id: number, request: UpdatePropertyAvailabilityRequest): Observable<PropertyAvailability> {
    return this.http.patch<PropertyAvailability>(`${this.apiUrl}/property/availability/${id}`, request);
  }

  // --------------------------------------------------------------
  // Internal Fetchers
  // --------------------------------------------------------------
  
  private fetchAvailability(id: string): Observable<PropertyAvailability[]> {
    return this.http.get<PropertyAvailability[]>(`${this.apiUrl}/property/availability/property/${id}`);
  }

  private fetchRules(id: string): Observable<PropertyPricingRule[]> {
    return this.http.get<PropertyPricingRule[]>(`${this.apiUrl}/property/pricing-rules/${id}`);
  }

  // --------------------------------------------------------------
  // Core Pricing Logic (Optimized)
  // --------------------------------------------------------------
  
  private buildPriceMap(
    avail: PropertyAvailability[] | null,
    rules: PropertyPricingRule[]
  ): Map<string, number> {
    const priceMap = new Map<string, number>();
    
    // 1. O(1) Lookup Map for Availability (fixes O(n^2) performance issue)
    const availArray = avail ?? [];
    const availLookup = new Map<string, number>();
    availArray.forEach(a => {
      if (a.priceCents != null) {
        availLookup.set(a.date, a.priceCents);
      }
    });

    // 2. Pre-parse Rules
    // Parse the daysOfWeekJson ONCE here, rather than 700+ times inside the loop
    const activeRules: ParsedPricingRule[] = rules
      .filter(r => r.isActive)
      .map(r => ({
        ...r,
        parsedDaysOfWeek: r.daysOfWeek ?? (r.daysOfWeekJson ? JSON.parse(r.daysOfWeekJson) : null)
      }))
      // Sort Lowest to Highest Priority. 
      // This ensures higher priority rules apply LAST and successfully overwrite lower ones.
      .sort((a, b) => a.priority - b.priority);

    const defaultRule = activeRules.find(r => r.startDate == null && r.endDate == null);

    // 3. Define Date Range
    const today = new Date();
    // Start from 1st day of last month
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    // End 2 years into the future
    const end = new Date(today.getFullYear() + 2, 0, 1);

    // 4. Single Pass Loop
    for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
      const isoDate = this.iso(current);
      const dayOfWeek = current.getUTCDay(); // 0 = Sunday, 6 = Saturday

      // Base price: explicitly set availability price, or 0
      let priceCents = availLookup.get(isoDate) ?? 0;

      // Apply Rules (High priority overwrites lower priority)
      for (const rule of activeRules) {
        if (this.ruleApplies(rule, isoDate, dayOfWeek)) {
          if (rule.priceCents != null) {
            priceCents = rule.priceCents; // Override flat rate
          } else if (rule.priceMultiplier != null && priceCents > 0) {
            priceCents = Math.round(priceCents * rule.priceMultiplier); // Apply multiplier
          }
        }
      }

      // Fallback to true default rule if still 0
      if (priceCents === 0 && defaultRule?.priceCents != null) {
        priceCents = defaultRule.priceCents;
      }

      // Add to map if valid
      if (priceCents > 0) {
        priceMap.set(isoDate, priceCents);
      }
    }

    return priceMap;
  }

  // --------------------------------------------------------------
  // Optimized Rule-matching Helper
  // --------------------------------------------------------------
  
  private ruleApplies(rule: ParsedPricingRule, iso: string, dayOfWeek: number): boolean {
    // 1. Date range bounds
    if (rule.startDate != null && iso < rule.startDate) return false;
    if (rule.endDate != null && iso > rule.endDate) return false;

    // 2. Day of Week check (uses the pre-parsed array)
    if (rule.parsedDaysOfWeek && rule.parsedDaysOfWeek.length > 0) {
      if (!rule.parsedDaysOfWeek.includes(dayOfWeek)) {
        return false;
      }
    }

    return true;
  }

  // --------------------------------------------------------------
  // Helper – ISO string
  // --------------------------------------------------------------
  
  private iso(date: Date): string {
    // Avoids timezone shifts by forcing local Date parts
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
// import { Injectable } from '@angular/core';
// import { HttpClient } from '@angular/common/http';
// import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
// import { map, shareReplay, switchMap } from 'rxjs/operators';
// import { AddPropertyAvailabilityRequest, PropertyAvailability, PropertyPricingRule, UpdatePropertyAvailabilityRequest } from '../models/property.model';
// import { environment } from '../../../../environments/environment';

// @Injectable({ providedIn: 'root' })
// export class AvailabilityService {
//   // --------------------------------------------------------------
//   // 1. Public streams
//   // --------------------------------------------------------------
//   private readonly propertyId$ = new BehaviorSubject<string | null>(null);
//   private readonly apiUrl = environment.apiUrl;

//   /** Map<ISO-date, final price in cents> */
//   readonly priceMap$: Observable<Map<string, number>> = this.propertyId$.pipe(
//     switchMap(id => (id ? this.loadData(id) : of(new Map<string, number>()))),
//     shareReplay(1)
//   );

//   /** Raw availability */
//   readonly avail$: Observable<PropertyAvailability[]> = this.propertyId$.pipe(
//     switchMap(id => (id ? this.fetchAvailability(id) : of([]))),
//     shareReplay(1)
//   );

//   constructor(private http: HttpClient) {}

//   createPropertyAvailability(availability: AddPropertyAvailabilityRequest) {
//     return this.http.post(`${this.apiUrl}/property/availability`, availability);
//   }

//   deletePropertyAvailability(id: number) {
//     return this.http.delete(`${this.apiUrl}/property/availability/${id}`);
//   }
//   // --------------------------------------------------------------
//   // 2. Public API
//   // --------------------------------------------------------------
//   setPropertyId(id: string): void {
//     this.propertyId$.next(id);
//   }

//   // --------------------------------------------------------------
//   // 3. Load + compute price map
//   // --------------------------------------------------------------
//   private loadData(propertyId: string): Observable<Map<string, number>> {
//     return combineLatest([
//       this.fetchAvailability(propertyId),
//       this.fetchRules(propertyId)
//     ]).pipe(
//       map(([avail, rules]) => {
//         //console.log('Availability data:', { avail, rules });
//         return this.buildPriceMap(avail, rules);
//       }),
//       shareReplay(1)
//     );
//   }

//   getPropertyAvailability(id: string): Observable<PropertyAvailability[]> {
//     // console.log('getPropertyAvailability', id);
//     return this.http.get<PropertyAvailability[]>(`${this.apiUrl}/property/availability/property/${id}`);
//   }

//   getPropertyAvailabilityById(id: number): Observable<PropertyAvailability> {
//     return this.http.get<PropertyAvailability>(`${this.apiUrl}/property/availability/${id}`);
//   }

//   updatePropertyAvailability(id: number, request: UpdatePropertyAvailabilityRequest): Observable<PropertyAvailability> {
//     return this.http.patch<PropertyAvailability>(`${this.apiUrl}/property/availability/${id}`, request);
//   }

//   private fetchAvailability(id: string): Observable<PropertyAvailability[]> {
//     return this.http
//       .get<PropertyAvailability[]>(`${this.apiUrl}/property/availability/property/${id}`)
//       .pipe(shareReplay(1));
//   }

//   private fetchRules(id: string): Observable<PropertyPricingRule[]> {
//     return this.http
//       .get<PropertyPricingRule[]>(`${this.apiUrl}/property/pricing-rules/${id}`)
//       .pipe(shareReplay(1));
//   }

//   // --------------------------------------------------------------
//   // 4. Core pricing logic
//   // --------------------------------------------------------------
//   private buildPriceMap(
//     avail: PropertyAvailability[] | null,
//     rules: PropertyPricingRule[]
//   ): Map<string, number> {
//     const map = new Map<string, number>();
//     const availArray = avail ?? [];

//     // Use `active` (your field)
//     const activeRules = rules
//       .filter(r => r.isActive)
//       .sort((b, a) => a.priority - b.priority);

//     // Default rule: no start/end date
//     const defaultRule = activeRules.find(r => r.startDate == null && r.endDate == null);

//     // console.log('Active rules:', activeRules);
//     // console.log('Default rule:', defaultRule);

//     // 1. Process availability records
//     // Instead of looping only over availArray, generate dates in relevant range
//     const today = new Date();
//     const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);   // ~2 months back
//     const end   = new Date(today.getFullYear() + 2, 0, 1);                  // ~2 years forward

//     let current = new Date(start);
//     while (current <= end) {
//       const isoDate = this.iso(current);

//       // Find base price (from availability if exists, else 0 or default later)
//       let priceCents = 0;
//       const availEntry = availArray.find(a => a.date === isoDate);
//       if (availEntry?.priceCents != null) {
//         priceCents = availEntry.priceCents;
//       }

//       // Apply rules in priority order (highest → lowest)
//       for (const r of activeRules) {
//         if (!this.ruleApplies(r, isoDate)) continue;

//         if (r.priceCents != null) {
//           priceCents = r.priceCents;
//         } else if (r.priceMultiplier != null && priceCents > 0) {
//           priceCents = Math.round(priceCents * r.priceMultiplier);
//         }
//       }

//       // If still 0 and we have a true default rule, use it
//       if (priceCents === 0 && defaultRule?.priceCents != null) {
//         priceCents = defaultRule.priceCents;
//       }

//       if (priceCents > 0) {
//         map.set(isoDate, priceCents);
//       }

//       current.setDate(current.getDate() + 1);
//     }

//     // 2. Fallback: fill missing dates with default rule
//     if (defaultRule?.priceCents != null) {
//       const defaultPrice = defaultRule.priceCents;
//       const today = new Date();
//       const start = new Date(today.getFullYear() - 1, 0, 1);
//       const end = new Date(today.getFullYear() + 1, 11, 31);

//       let current = new Date(start);
//       while (current <= end) {
//         const iso = this.iso(current);
//         if (!map.has(iso)) {
//           map.set(iso, defaultPrice);
//         }
//         current = new Date(current);
//         current.setDate(current.getDate() + 1);
//       }
//     }

//     // console.log('Price map size:', map.size);
//     // console.log('Sample price 2025-11-27:', map.get('2025-11-27'));

//     // console.log('=== PRICING DEBUG ===');
//     // console.log('Rules received:', rules);
//     // console.log('Active rules:', activeRules);
//     // console.log('Default rule:', defaultRule);
//     // console.log('Availability count:', availArray.length);

//     return map;
//   }

//   // --------------------------------------------------------------
//   // 5. Rule-matching helper
//   // --------------------------------------------------------------
//   private ruleApplies(rule: PropertyPricingRule, iso: string): boolean {
//     const d = new Date(iso);
//     const day = d.getUTCDay();

//     // Use == null to match null or undefined
//     if (rule.startDate != null && iso < rule.startDate) return false;
//     if (rule.endDate != null && iso > rule.endDate) return false;

//     const dow = rule.daysOfWeek ??
//       (rule.daysOfWeekJson ? JSON.parse(rule.daysOfWeekJson) : null);

//     // Allow [] or null = match all days
//     if (dow && dow.length > 0 && !dow.includes(day)) return false;

// const applies =
//     (rule.startDate == null || iso >= rule.startDate) &&
//     (rule.endDate == null || iso <= rule.endDate) &&
//     (!rule.daysOfWeek?.length || rule.daysOfWeek.includes(day));

//   //   if (iso === '2026-03-07') {   // example debug date
//   //   console.log(`Rule "${rule.name}" (prio ${rule.priority}) for ${iso}:`, {
//   //     dateOk: iso >= (rule.startDate ?? 'any') && iso <= (rule.endDate ?? 'any'),
//   //     dayOk: rule.daysOfWeek?.includes(day) ?? true,
//   //     dayNumber: day,
//   //     applies
//   //   });
//   // }

//     return true;
//   }

//   // --------------------------------------------------------------
//   // 6. Helper – ISO string
//   // --------------------------------------------------------------
//   private iso(date: Date): string {
//     return date.toISOString().split('T')[0]; // "2025-11-27"
//   }
// }